import { useCabinetMemberMe } from '@/hooks/useCabinetMemberMe';
import { useMarkAnnouncementsSeen } from './useAnnouncements';
import { ANNOUNCEMENTS } from './announcements';
import type { Announcement } from './types';

export interface AnnouncementItem extends Announcement { read: boolean; }

/** Dérivation pure : une annonce est "non lue" ssi absente de `seen` ET
 *  postérieure ou égale à l'arrivée du membre (granularité jour UTC).
 *  Les annonces antérieures à `joinedAt` sont marquées `read` sans écriture DB
 *  (informational) : visibles au panneau, mais n'allument pas la pastille. */
export function deriveItems(
  catalog: Announcement[], seen: string[], joinedAt: string,
): { items: AnnouncementItem[]; unreadCount: number } {
  const seenSet = new Set(seen);
  const joinedDay = joinedAt.slice(0, 10);
  const items = catalog.map((a) => ({
    ...a, read: seenSet.has(a.id) || a.date < joinedDay,
  }));
  return { items, unreadCount: items.filter((i) => !i.read).length };
}

/** Hook composant-facing. `null` tant que la membership n'est pas résolue
 *  (même garde que useLatestUnseenAnnouncement). */
export function useAnnouncementCenter() {
  const { data } = useCabinetMemberMe();
  const markSeen = useMarkAnnouncementsSeen();
  if (!data?.joined_at) return null;
  const { items, unreadCount } = deriveItems(
    ANNOUNCEMENTS, data.seen_announcements ?? [], data.joined_at,
  );
  const readIds = new Set(items.filter((i) => i.read).map((i) => i.id));
  return {
    items, readIds, unreadCount,
    markRead: (id: string) => markSeen.mutate([id]),
    // "Tout marquer lu" : seules les non-lues éligibles (pas les pré-join)
    markAllRead: () => markSeen.mutate(items.filter((i) => !i.read).map((i) => i.id)),
  };
}
